import React, { useState, useEffect } from 'react';
import { RefreshCw, Search, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { toast } from 'react-toastify';
import PeriodFilter from '../orders/PeriodFilter';
import { exportOrdersExcel, excelDate } from '../orders/exportOrders';
import { formatCurrency, formatNumber } from '../../utils/currency';
import { formatTableDate } from '../../utils/dateFormatter';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';
const PAGE_SIZE = 500;

export function AmazonOrderList({ storeId }) {
  const [orders, setOrders]           = useState([]);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [syncing, setSyncing]         = useState(false);
  const [search, setSearch]           = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [channelFilter, setChannelFilter] = useState(''); // '' | 'MFN' | 'AFN'

  const today        = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const todayStr     = today.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate]     = useState(todayStr);
  const [loadedRange, setLoadedRange] = useState([firstOfMonth, todayStr]);

  const activeStoreId = storeId || localStorage.getItem('amazon_store_id');

  useEffect(() => {
    if (activeStoreId) { fetchOrders(); fetchStats(); }
  }, [activeStoreId]);

  // Re-fetch when channel filter changes
  useEffect(() => {
    if (activeStoreId) { fetchOrders(startDate, endDate); fetchStats(startDate, endDate); }
  }, [channelFilter]);

  const buildParams = (start, end) => {
    const params = new URLSearchParams();
    if (start && end) { params.append('start_date', start); params.append('end_date', end + ' 23:59:59'); }
    if (channelFilter) params.append('fulfillment_channel', channelFilter);
    return params;
  };

  const fetchOrders = async (start = startDate, end = endDate) => {
    try {
      setLoading(true);
      const token    = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/amazon/${activeStoreId}/orders?${buildParams(start, end)}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) { setOrders(data.orders); setLoadedRange([start, end]); setCurrentPage(1); }
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const fetchStats = async (start = startDate, end = endDate) => {
    try {
      const token    = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/amazon/${activeStoreId}/orders/stats?${buildParams(start, end)}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) setStats(data.stats);
    } catch { console.error('Failed to load stats'); }
  };

  const handleFilter = (s = startDate, e = endDate) => { setStartDate(s); setEndDate(e); fetchOrders(s, e); fetchStats(s, e); };
  const handleDownload = () => {
    if (!filteredOrders.length) return toast.info('No orders to download for this period');
    const channel = channelFilter === 'AFN' ? ' FBA' : channelFilter === 'MFN' ? ' FBM' : '';
    exportOrdersExcel({
      title: `Amazon Orders${channel}`, orders: filteredOrders, startDate: loadedRange[0], endDate: loadedRange[1],
      orderKey: o => o.amazon_order_id,
      columns: [
        { header: 'Order ID', value: o => o.amazon_order_id, width: 22 },
        { header: 'Date', value: o => excelDate(o.purchase_date), width: 18 },
        { header: 'Product', value: o => o.product_name, width: 50 },
        { header: 'SKU', value: o => o.seller_sku, width: 18 },
        { header: 'ASIN', value: o => o.asin, width: 13 },
        { header: 'Channel', value: o => (o.fulfillment_channel === 'AFN' ? 'FBA' : 'FBM'), width: 8 },
        { header: 'Qty', value: o => o.quantity, sum: true, width: 6 },
        { header: 'Revenue', value: o => o.total_revenue, money: true },
        { header: 'Cost', value: o => o.cost_price, money: true },
        { header: 'Fees', value: o => o.amazon_fees, money: true },
        { header: 'Profit', value: o => o.net_profit, money: true },
        { header: 'Status', value: o => o.order_status },
      ],
    });
  };
  const handleClearFilter = () => { setStartDate(''); setEndDate(''); fetchOrders('', ''); fetchStats('', ''); };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const token    = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/amazon/${activeStoreId}/orders/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.success) { toast.success(`Synced ${data.synced} order items!`); fetchOrders(); fetchStats(); }
      else toast.error('Sync failed');
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const getProfitMargin = () => {
    if (!stats || !stats.total_revenue || stats.total_revenue === 0) return '0.0';
    return ((parseFloat(stats.total_profit || 0) / parseFloat(stats.total_revenue)) * 100).toFixed(1);
  };

  const filteredOrders = orders.filter(order => {
    const q = search.toLowerCase();
    return (
      order.product_name?.toLowerCase().includes(q) ||
      order.amazon_order_id?.toLowerCase().includes(q) ||
      order.seller_sku?.toLowerCase().includes(q) ||
      order.asin?.toLowerCase().includes(q)
    );
  });

  const totalPages      = Math.ceil(filteredOrders.length / PAGE_SIZE);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const fbaCount = orders.filter(o => o.fulfillment_channel === 'AFN').length;
  const fbmCount = orders.filter(o => o.fulfillment_channel !== 'AFN').length;

  return (
    <div className="p-8 bg-gray-50 min-h-screen">

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Amazon Orders</h1>
          <p className="text-gray-600">
            {search ? `${filteredOrders.length} of ${orders.length} order items` : `${orders.length} order items displayed`}
          </p>
        </div>
        <button onClick={handleSync} disabled={syncing}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all shadow-lg disabled:opacity-50">
          <RefreshCw className={`mr-2 ${syncing ? 'animate-spin' : ''}`} size={20} />
          {syncing ? 'Syncing...' : 'Sync Orders'}
        </button>
      </div>

      {/* FBA / FBM filter tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { label: `All (${orders.length})`, value: '' },
          { label: `FBM (${fbmCount})`,       value: 'MFN' },
          { label: `FBA (${fbaCount})`,        value: 'AFN' },
        ].map(c => (
          <button key={c.value} onClick={() => setChannelFilter(c.value)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold border transition-all duration-150 ${
              channelFilter === c.value
                ? c.value === 'AFN' ? 'bg-blue-600 text-white border-blue-600'
                : c.value === 'MFN' ? 'bg-orange-600 text-white border-orange-600'
                : 'bg-gray-700 text-white border-gray-700'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
            }`}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <p className="text-gray-600 mb-2">Total Orders</p>
            <p className="text-3xl font-bold text-purple-600">{formatNumber(stats.total_orders)}</p>
            <p className="text-sm text-gray-500 mt-1">{formatNumber(stats.total_items)} units sold</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <p className="text-gray-600 mb-2">Total Revenue</p>
            <p className="text-3xl font-bold text-orange-600">{formatCurrency(stats.total_revenue)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <p className="text-gray-600 mb-2">Total Profit</p>
            <p className="text-3xl font-bold text-green-600">{formatCurrency(stats.total_profit)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <p className="text-gray-600 mb-2">Profit Margin</p>
            <p className="text-3xl font-bold text-blue-600">{getProfitMargin()}%</p>
          </div>
        </div>
      )}

      {/* Date Filter + Search */}
      <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <PeriodFilter startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate}
            onApply={handleFilter} color="orange" />
          <button onClick={handleClearFilter}
            className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium">
            Show All
          </button>
          <button onClick={handleDownload} disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50">
            <Download size={16} /> Download Excel
          </button>
          <div className="flex-1 min-w-48 relative">
            <label className="block text-gray-600 text-sm font-medium mb-1">Search</label>
            <Search className="absolute left-3 bottom-2.5 text-gray-400" size={18} />
            <input type="text" value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              placeholder="Order ID, product, SKU or ASIN..."
              className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" />
            {search && (
              <button onClick={() => { setSearch(''); setCurrentPage(1); }}
                className="absolute right-3 bottom-2.5 text-gray-400 hover:text-gray-600">✕</button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-orange-600 mr-2" size={24} />
              <span className="text-gray-500">Loading orders...</span>
            </div>
          ) : paginatedOrders.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="text-left py-4 px-6 text-gray-600 font-semibold">Order ID</th>
                  <th className="text-left py-4 px-6 text-gray-600 font-semibold">Product</th>
                  <th className="text-center py-4 px-6 text-gray-600 font-semibold">Channel</th>
                  <th className="text-center py-4 px-6 text-gray-600 font-semibold">Qty</th>
                  <th className="text-right py-4 px-6 text-gray-600 font-semibold">Revenue</th>
                  <th className="text-right py-4 px-6 text-gray-600 font-semibold">Cost</th>
                  <th className="text-right py-4 px-6 text-gray-600 font-semibold">Fees</th>
                  <th className="text-right py-4 px-6 text-gray-600 font-semibold">Profit</th>
                  <th className="text-left py-4 px-6 text-gray-600 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order) => (
                  <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6 font-medium text-gray-800 font-mono text-xs">{order.amazon_order_id}</td>
                    <td className="py-4 px-6 text-gray-700">
                      <p className="font-medium">{order.product_name}</p>
                      <p className="text-xs text-gray-500">ASIN: {order.asin}</p>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        order.fulfillment_channel === 'AFN' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {order.fulfillment_channel === 'AFN' ? 'FBA' : 'FBM'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center font-medium">{order.quantity}</td>
                    <td className="py-4 px-6 text-right font-semibold text-orange-600">{formatCurrency(order.total_revenue)}</td>
                    <td className="py-4 px-6 text-right text-gray-700">{formatCurrency(order.cost_price)}</td>
                    <td className="py-4 px-6 text-right text-red-600">{formatCurrency(order.amazon_fees)}</td>
                    <td className="py-4 px-6 text-right font-bold text-green-600">{formatCurrency(order.net_profit)}</td>
                    <td className="py-4 px-6 text-gray-600">{formatTableDate(order.purchase_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              {search ? (
                <><p className="text-gray-500 text-lg mb-2">No orders found for "{search}"</p>
                <p className="text-gray-400 text-sm">Try a different search term</p></>
              ) : (
                <><p className="text-gray-500 text-lg mb-4">No orders found</p>
                <p className="text-gray-400 text-sm">Try adjusting the date filter or sync orders</p></>
              )}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filteredOrders.length)} of {filteredOrders.length} orders
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40">
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx-1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
                .map((p, idx) => p === '...' ? (
                  <span key={`e-${idx}`} className="px-2 text-gray-400">...</span>
                ) : (
                  <button key={p} onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1 rounded-lg border text-sm font-medium ${currentPage === p ? 'bg-orange-600 text-white border-orange-600' : 'border-gray-300 hover:bg-gray-100 text-gray-700'}`}>
                    {p}
                  </button>
                ))}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AmazonOrderList;